import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';

import { getCostTemplates, createCostTemplate, deleteCostTemplate } from '../../../services/supabaseService';

const CostTemplatesPanel = ({ onApplyTemplate }) => {
  const { currentLanguage, t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    groupType: 'Omra',
    templateData: []
  });
  const [tempCostItems, setTempCostItems] = useState([
    { category: 'accommodation', description: '', amount: '' },
    { category: 'transport', description: '', amount: '' },
    { category: 'meals', description: '', amount: '' }
  ]);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await getCostTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate?.name) {
      alert(currentLanguage === 'fr' ? 'Veuillez entrer un nom' : 'يرجى إدخال اسم');
      return;
    }

    const validItems = tempCostItems?.filter(item => item?.description && item?.amount);
    if (validItems?.length === 0) {
      alert(currentLanguage === 'fr' ? 'Ajoutez au moins un élément de coût' : 'أضف عنصر تكلفة واحد على الأقل');
      return;
    }

    try {
      await createCostTemplate({
        ...newTemplate,
        templateData: validItems
      });
      
      const successMessage = currentLanguage === 'fr' ?'Modèle créé avec succès' :'تم إنشاء النموذج بنجاح';
      alert(successMessage);
      
      setShowCreateModal(false);
      setNewTemplate({ name: '', description: '', groupType: 'Omra', templateData: [] });
      setTempCostItems([
        { category: 'accommodation', description: '', amount: '' },
        { category: 'transport', description: '', amount: '' },
        { category: 'meals', description: '', amount: '' }
      ]);
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      alert(currentLanguage === 'fr' ? 'Erreur lors de la création' : 'خطأ في الإنشاء');
    }
  };

  const handleDeleteTemplate = async (id) => {
    const confirmMessage = currentLanguage === 'fr' ?'Êtes-vous sûr de vouloir supprimer ce modèle?' :'هل أنت متأكد من حذف هذا النموذج؟';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await deleteCostTemplate(id);
      const successMessage = currentLanguage === 'fr' ?'Modèle supprimé' :'تم حذف النموذج';
      alert(successMessage);
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const addCostItem = () => {
    setTempCostItems([...tempCostItems, { category: 'other', description: '', amount: '' }]);
  };

  const removeCostItem = (index) => {
    setTempCostItems(tempCostItems?.filter((_, i) => i !== index));
  };

  const updateCostItem = (index, field, value) => {
    const updated = [...tempCostItems];
    updated[index][field] = value;
    setTempCostItems(updated);
  };

  const categoryOptions = [
    { value: 'accommodation', label: t('financial.accommodation') },
    { value: 'transport', label: t('financial.transport') },
    { value: 'meals', label: t('financial.meals') },
    { value: 'guides', label: t('financial.guides') },
    { value: 'visas', label: t('financial.visasCost') },
    { value: 'other', label: t('financial.other') }
  ];

  const getCategoryLabel = (category) => {
    return categoryOptions?.find(opt => opt?.value === category)?.label || category;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {t('financial.costTemplates')}
        </h3>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateModal(true)}
        >
          {t('financial.createTemplate')}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          {currentLanguage === 'fr' ? 'Chargement...' : 'جاري التحميل...'}
        </div>
      ) : templates?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {currentLanguage === 'fr' ? 'Aucun modèle disponible' : 'لا توجد نماذج متاحة'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates?.map((template) => (
            <div key={template?.id} className="border border-border rounded-lg p-4 hover:shadow-warm-md transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-foreground">{template?.name}</h4>
                  <p className="text-sm text-muted-foreground">{template?.description}</p>
                  <span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                    {template?.groupType}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleDeleteTemplate(template?.id)}
                />
              </div>
              
              <div className="space-y-2 mb-3">
                {template?.templateData?.slice(0, 3)?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{getCategoryLabel(item?.category)}</span>
                    <span className="font-medium text-foreground data-text">{parseFloat(item?.amount)?.toFixed(2)} MAD</span>
                  </div>
                ))}
                {template?.templateData?.length > 3 && (
                  <p className="text-xs text-muted-foreground">
                    +{template?.templateData?.length - 3} {currentLanguage === 'fr' ? 'autres' : 'آخرين'}
                  </p>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Copy"
                iconPosition="left"
                onClick={() => onApplyTemplate?.(template)}
              >
                {t('financial.applyTemplate')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-warm-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-xl font-semibold text-foreground">
                {t('financial.createTemplate')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setShowCreateModal(false)}
              />
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('financial.templateName')}
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newTemplate?.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e?.target?.value })}
                  placeholder={currentLanguage === 'fr' ? 'Nom du modèle' : 'اسم النموذج'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('financial.description')}
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newTemplate?.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e?.target?.value })}
                  placeholder={currentLanguage === 'fr' ? 'Description' : 'الوصف'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('financial.groupType')}
                </label>
                <select
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newTemplate?.groupType}
                  onChange={(e) => setNewTemplate({ ...newTemplate, groupType: e?.target?.value })}
                >
                  <option value="Omra">Omra</option>
                  <option value="Hajj">Hajj</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t('financial.costItems')}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Plus"
                    onClick={addCostItem}
                  >
                    {t('financial.addItem')}
                  </Button>
                </div>
                <div className="space-y-3">
                  {tempCostItems?.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <select
                        className="px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        value={item?.category}
                        onChange={(e) => updateCostItem(index, 'category', e?.target?.value)}
                      >
                        {categoryOptions?.map(opt => (
                          <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        value={item?.description}
                        onChange={(e) => updateCostItem(index, 'description', e?.target?.value)}
                        placeholder={currentLanguage === 'fr' ? 'Description' : 'الوصف'}
                      />
                      <input
                        type="number"
                        className="w-32 px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        value={item?.amount}
                        onChange={(e) => updateCostItem(index, 'amount', e?.target?.value)}
                        placeholder="0.00"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => removeCostItem(index)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t('financial.cancel')}
                </Button>
                <Button
                  variant="default"
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleCreateTemplate}
                >
                  {t('financial.createTemplate')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostTemplatesPanel;